import React, { useState, useEffect } from "react";
import { Link as ScrollLink } from "react-scroll";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faBars, faTimes, faSun, faMoon } from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import RobotLogo from "./RobotLogo";
import { useBooking } from "../context/BookingContext";

const Navbar = () => {
  const { openBooking } = useBooking();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Default to dark; respect saved preference if it exists
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved ? saved === "dark" : true;
  });

  // Apply / remove .dark on <html>
  useEffect(() => {
    const html = document.documentElement;
    if (isDark) {
      html.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      html.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDark]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { to: "services",     label: "Services" },
    { to: "case-studies", label: "Case Studies" },
    { to: "skills",       label: "Tech Stack" },
  ];

  return (
    <div className="fixed top-3 sm:top-5 left-0 right-0 z-50 flex justify-center px-3 sm:px-4 pointer-events-none">
      <header
        className={`pointer-events-auto w-full max-w-[calc(100vw-1.5rem)] sm:max-w-3xl lg:max-w-fit transition-all duration-300 rounded-3xl lg:rounded-full border ${
          scrolled
            ? "bg-background/90 backdrop-blur-xl shadow-elevated border-border py-2 px-3 sm:px-4"
            : "bg-background/50 backdrop-blur-md shadow-soft border-border/30 py-2.5 px-3 sm:px-5 lg:px-6"
        }`}
      >
        <nav className="flex items-center justify-between gap-3 lg:gap-8">
          {/* Logo */}
          <ScrollLink to="mainy" spy smooth offset={-80} duration={600} className="cursor-pointer">
            <RobotLogo size={32} />
          </ScrollLink>

          {/* Desktop nav */}
          <div className="hidden lg:flex space-x-1 items-center">
            {navItems.map((item) => (
              <ScrollLink
                key={item.to}
                to={item.to}
                spy smooth offset={-80} duration={600}
                activeClass="bg-primary/10 text-primary font-bold shadow-sm"
                className="cursor-pointer px-4 py-2 text-muted-foreground hover:text-primary transition-colors font-bold text-sm rounded-full hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {item.label}
              </ScrollLink>
            ))}

            <div className="w-px h-5 bg-border mx-2" />

            {/* Theme toggle */}
            <button
              onClick={() => setIsDark(!isDark)}
              className="p-2 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/5 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              <FontAwesomeIcon icon={isDark ? faSun : faMoon} className="w-4 h-4" />
            </button>

            <a
              href="/files/Muhammad_Akash_Zaheer_FullStack_Engineer.pdf"
              target="_blank"
              rel="noreferrer"
              className="ml-1 px-4 py-2 text-foreground font-bold text-sm flex items-center gap-2 hover:bg-primary/5 hover:text-primary rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <FontAwesomeIcon icon={faDownload} className="w-3 h-3" />
              Resume
            </a>

            <button
              onClick={openBooking}
              className="ml-2 px-5 py-2 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-full font-bold text-sm hover:shadow-glow hover:-translate-y-0.5 transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              id="nav-cta"
            >
              Book a Strategy Call
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2 text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            <FontAwesomeIcon icon={menuOpen ? faTimes : faBars} className="w-5 h-5" />
          </button>
        </nav>

        {/* Mobile menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute top-full left-0 right-0 mt-3 glass-card rounded-2xl flex flex-col items-center py-4 gap-2 lg:hidden pointer-events-auto"
            >
              {navItems.map((item) => (
                <ScrollLink
                  key={item.to}
                  to={item.to}
                  spy smooth offset={-80} duration={600}
                  activeClass="text-primary font-bold bg-primary/10"
                  className="py-2.5 px-4 w-[90%] text-center font-bold text-muted-foreground hover:text-primary transition rounded-lg"
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </ScrollLink>
              ))}

              <div className="w-[80%] border-t border-border my-2" />

              <button
                onClick={() => { setIsDark(!isDark); setMenuOpen(false); }}
                className="py-2.5 px-4 w-[90%] text-center font-bold text-muted-foreground hover:text-primary transition flex items-center justify-center gap-2 rounded-lg"
              >
                <FontAwesomeIcon icon={isDark ? faSun : faMoon} className="w-4 h-4" />
                {isDark ? "Light Mode" : "Dark Mode"}
              </button>

              <a
                href="/files/Muhammad_Akash_Zaheer_FullStack_Engineer.pdf"
                target="_blank"
                rel="noreferrer"
                className="py-2.5 px-4 w-[90%] text-center font-bold text-foreground hover:bg-primary/5 hover:text-primary rounded-lg transition"
              >
                Resume
              </a>

              <button
                onClick={() => {
                  setMenuOpen(false);
                  openBooking();
                }}
                className="mt-2 px-6 py-3 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-xl font-bold text-sm w-[90%] hover:shadow-glow transition-all"
              >
                Book a Strategy Call
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </div>
  );
};

export default Navbar;
